import { Button, Col, Form, Row, Switch, message } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AttributeBox from "../../../commons/components/AttributeBox/AttributeBox";
import BorderGradientButton from "../../../commons/components/BorderGradientButton/BorderGradientButton";
import CustomDropImageInput from "../../../commons/components/CustomDropImageInput/CustomDropImageInput";
import CustomInput from "../../../commons/components/CustomInput/CustomInput";
import { CustomSelect } from "../../../commons/components/CustomSelect/CustomSelect";
import FormBox from "../../../commons/components/FormBox/FormBox";
import FormHeader from "../../../commons/components/FormHeader/FormHeader";
import GradientText from "../../../commons/components/GradientText/GradientText";
import TextArea from "../../../commons/components/TextArea/TextArea";
import {
  MAIN_TAG_OPTIONS,
  PRODUCT_VARIANTS,
  ProductStatusEnum,
  TYPE_OF_ATTRIBUTE,
} from "../../../commons/constants/product";
import defaultAxios from "../../../commons/utils/axios";
import {
  decimalOnlyInput,
  filterECharacterInputDecimal,
  filterECharacterInputNumber,
  integerOnlyInput,
} from "../../../commons/utils/functions/integerOnly";
import {
  calculateCostPrice,
  calculateSellingPrice,
  createNewAttribute,
  getBodyFromProductValues,
  getValuesFromProductDetail,
} from "../../../commons/utils/functions/productMapping";
import removeExtraSpace from "../../../commons/utils/functions/removeExtraSpace";
import { uploadImageToServer } from "../../../commons/utils/functions/uploadImageToServer";
import { isPositiveNumberOrZero } from "../../../commons/utils/functions/validation";
import { AppContext } from "../../../contexts/AppContext";
import PreviewProduct from "../../PreviewProduct/PreviewProduct";
import styles from "./CreateUpdateProductForm.module.scss";

import BigNumber from "bignumber.js";
import { LETTER_REGEX_ALLOW_DECIMAL, MediaDirectoryEnum, StoreExample } from "../../../commons/constants";
import {
  CREATE_PRODUCT_DRAFT_URL,
  CREATE_PRODUCT_URL,
  LIST_MENU_OF_STORE,
  MARKUP_FEES,
  UPDATE_PRODUCT_URL,
} from "../../../commons/constants/api-urls";
import { routerNavigation } from "../../../commons/constants/routers";
import CustomRadio from "../../../commons/components/CustomRadio/CustomRadio";
import RadioBoxList from "../../../commons/components/RadioBoxList/RadioBoxList";
import { BINARY_OPTIONS } from "../../../commons/constants/store";

type Props = {
  product?: Product | null;
};

const CreateUpdateProductForm = ({ product }: Props) => {
  const { user, currentStore, configAttributes, currentStoreDetail } = useContext(AppContext);

  const navigate = useNavigate();
  const [form] = Form.useForm<CreateUpdateProductValues>();
  const [markup, setMarkup] = useState(0);
  const isLoadAttributeFirstTime = useRef<boolean>(false);
  const [preview, setPreview] = useState<boolean>(false);
  const [isFormLoad, setIsFormLoad] = useState<boolean>(false);
  const [isMatchRatio, setIsMatchRatio] = useState<boolean[]>([]);
  const attr = Form.useWatch("attributes", form);
  const price = Form.useWatch("price", form);
  const costPrice = Form.useWatch("costPrice", form);
  const [loading, setLoading] = useState<"draft" | "save" | false>(false);
  const [productPreview, setProductPreview] = useState<CreateUpdateProductBody | null>(null);
  const [isSellingMode, setIsSellingMode] = useState(false);
  const [listGroupMenu, setListGroupMenu] = useState<{ value: string; label: string }[]>([]);
  const oldStatus: number | undefined = product ? product.status : undefined;

  async function handleGetListGroupMenu() {
    try {
      const { data } = await defaultAxios.get<GroupMenuList[]>(LIST_MENU_OF_STORE(currentStore.id));
      const list = data.map(item => ({ label: item.name, value: item.id }));
      setListGroupMenu(list);
    } catch (error) {
      message.error("Can't get list menu");
      console.log(error);
    }
  }

  async function handleGetMarkup() {
    try {
      const res = await defaultAxios.get(MARKUP_FEES(currentStore?.id));
      setMarkup(res.data.mark_up);
    } catch (error) {
      message.error("Get markup failed");
    }
  }

  async function editDraftProduct(body: CreateUpdateProductBody) {
    try {
      const realBody = {
        ...body,
        status: ProductStatusEnum.DRAFT,
      };
      await defaultAxios.put<CreateUpdateProductResponse>(UPDATE_PRODUCT_URL((product as any)?.id), realBody);
      message.success(`Update draft product successfully`);
    } catch (error) {
      message.error(`Update draft product failed`);
    }
  }
  async function editActiveProduct(body: CreateUpdateProductBody) {
    try {
      const realBody = {
        ...body,
        status: oldStatus,
      };
      await defaultAxios.put<CreateUpdateProductResponse>(UPDATE_PRODUCT_URL((product as any)?.id), realBody);
      if (oldStatus === ProductStatusEnum.ACTIVE) {
        message.success("Please wait for admin approval of your request  ");
      } else {
        message.success(`Update product successfully`);
      }
    } catch (error) {
      message.error(`Update product failed`);
    }
  }
  async function createNewProduct(body: CreateUpdateProductBody) {
    try {
      const realBody = {
        ...body,
        status: ProductStatusEnum.ACTIVE,
      };
      // create new change
      await defaultAxios.post<CreateUpdateProductResponse>(CREATE_PRODUCT_URL, realBody);
      message.success("Please wait for admin approval of your request  ");
    } catch (error) {
      message.error(` Create product failed`);
    }
  }
  async function createNewDraftProduct(body: CreateUpdateProductBody) {
    try {
      const realBody = {
        ...body,
        status: ProductStatusEnum.DRAFT,
      };
      await defaultAxios.post<CreateUpdateProductResponse>(CREATE_PRODUCT_DRAFT_URL, realBody);
      message.success(`Create new draft product successfully`);
    } catch (error) {
      message.error(`Save draft product failed`);
    }
  }

  const onFinish = async (values: CreateUpdateProductValues, isDraft?: boolean) => {
    if (!currentStore || currentStore.id === StoreExample.id) {
      message.error(`Please select store`);
      return;
    }
    setLoading(isDraft ? "draft" : "save");

    const { images } = values;
    const imageUrls = images.filter(image => typeof image === "string") as string[];

    const newImages = images.filter(image => typeof image !== "string" && !Array.isArray(image));

    if (newImages) {
      try {
        await uploadImageToServer(newImages, MediaDirectoryEnum.PRODUCT).then(res => {
          if (Array.isArray(res)) {
            res.forEach(item => imageUrls.push(item));
          } else {
            imageUrls.push(res);
          }
        });
      } catch (error) {
        setLoading(false);
        message.error("Upload image failed");
      }
    }

    const attributeJSON = localStorage.getItem(PRODUCT_VARIANTS) || "";
    const attributeArr = attributeJSON ? JSON.parse(attributeJSON) : [];

    attributeArr.forEach((attributeLocal: any) => {
      const attIndex = values.attributes.findIndex(item => item.attribute_name === attributeLocal.attribute_name);

      if (attIndex !== -1) {
        attributeLocal.variants.forEach((variantLocal: any) => {
          const variantIdx = values.attributes[attIndex].variants.findIndex(
            (variant: any) => variant.attribute_value === variantLocal.attribute_value
          );

          if (variantIdx === -1) {
            values.attributes[attIndex].variants = [
              ...values.attributes[attIndex].variants,
              { ...variantLocal, is_deleted: true },
            ];
          }
        });
      }
    });

    try {
      const body: CreateUpdateProductBody = {
        ...getBodyFromProductValues(isSellingMode, values, currentStore.id),
        images: imageUrls,
        thumbnail: imageUrls[0],
        groupMenuId: values.groupMenuId ? values.groupMenuId : undefined,
      };

      const isEdit = Number(product?.id) > 0;

      if (isEdit) {
        // edit to draft => no change
        if (isDraft) {
          editDraftProduct(body);
        } else {
          // Edit active product => need approval
          editActiveProduct(body);
        }
      } else {
        // create new change
        if (isDraft) {
          createNewDraftProduct(body);
        } else {
          createNewProduct(body);
        }
      }
      navigate(routerNavigation.STORES.PRODUCT_MANAGEMENT(currentStore.id.toString()));
    } catch (error: any) {
      console.log(error);
      message.error(`${product?.id ? "Update" : "Create"} product failed`);
    }
    setLoading(false);
  };

  const onDraft = async () => {
    setLoading("draft");
    try {
      const values = await form.validateFields();
      onFinish(values, true);
    } catch (error) {
      onFinishFailed(error);
    }
    setLoading(false);
  };

  const onFinishFailed = (errorInfo: any) => {
    const namePath = errorInfo.errorFields[0].name;
    form.getFieldInstance(namePath)?.focus();
  };

  function onChangeSwitch(checked: boolean) {
    setIsSellingMode(checked);
  }

  function handleFormatPrice() {
    if (!price) return;

    if (isNaN(Number(price))) {
      //stupid typescript, try to remove Number to see the result
      form.setFieldValue("price", undefined);
      return;
    }

    if (Number(price) === 0) {
      form.setFieldValue("price", Number(price).toFixed(2));
    }

    const priceAfterDecimal = price.toString().split(".");

    if (priceAfterDecimal[1]?.length > 2) {
      // form.setFieldValue("price", (Math.round(Number(price) * 100) / 100).toFixed(2));
      form.setFieldValue("price", (Math.floor(Number(price) * 100) / 100).toFixed(2));
    } else {
      form.setFieldValue("price", Number(price).toFixed(2));
    }

    if (Number(price) > 0) {
      form.setFields([{ name: "price", errors: undefined }]);
    }
  }

  function handleFormatCostPrice() {
    if (!costPrice) return;

    if (isNaN(Number(costPrice))) {
      //stupid typescript, try to remove Number to see the result
      form.setFieldValue("costPrice", undefined);
      return;
    }

    if (Number(costPrice) === 0) {
      form.setFieldValue("costPrice", Number(costPrice).toFixed(2));
    }

    const costPriceAfterDecimal = costPrice.toString().split(".");

    if (costPriceAfterDecimal[1]?.length > 2) {
      // form.setFieldValue("costPrice", (Math.round(Number(costPrice) * 100) / 100).toFixed(2));
      form.setFieldValue("costPrice", (Math.floor(Number(costPrice) * 100) / 100).toFixed(2));
    } else {
      form.setFieldValue("costPrice", Number(costPrice).toFixed(2));
    }

    if (Number(costPrice) > 0) {
      form.setFields([{ name: "costPrice", errors: undefined }]);
    }
  }

  const handleOnChangeInputNumberCostPrice = (name: string, event: any) => {
    const costPrice = event.target.value && event.target.value.replace(LETTER_REGEX_ALLOW_DECIMAL, "");
    const sellingPrice = Number(costPrice || 0) * (1 + markup / 100);
    const bigInt = new BigNumber(sellingPrice);
    form.setFieldValue(name, costPrice);
    form.setFieldValue("price", calculateSellingPrice(costPrice, markup));
  };

  const handleOnChangeInputNumberSellingPrice = (name: string, event: any) => {
    const sellingPrice = event.target.value && event.target.value.replace(LETTER_REGEX_ALLOW_DECIMAL, "");
    const costPrice = Number((sellingPrice || 0) / (1 + markup / 100));
    const bigInt = new BigNumber(costPrice);
    form.setFieldValue(name, sellingPrice);
    form.setFieldValue("costPrice", calculateCostPrice(sellingPrice, markup));
  };

  const renderCateList = () => {
    const cateList = [...currentStoreDetail.categoriesLevel1];
    return cateList.map((item, idx) => {
      if (!item) return null;
      if (idx === cateList.length - 1) return <div key={idx}>{item.name}.</div>;
      return <div key={idx}>{item.name},</div>;
    });
  };

  useEffect(() => {
    // cache product variants from DB on localStorage
    product && localStorage.setItem(PRODUCT_VARIANTS, JSON.stringify(product.attributes));
    return () => {
      localStorage.removeItem(PRODUCT_VARIANTS);
    };
  }, [product]);

  useEffect(() => {
    if (isFormLoad || !product || !markup) return;

    setIsSellingMode(product.isSellingMode);
    form.setFieldsValue(getValuesFromProductDetail(markup, product, currentStore?.id, configAttributes));

    setIsFormLoad(true);
  }, [product, currentStore?.id, form, configAttributes, markup]);

  useEffect(() => {
    if (currentStore.id !== StoreExample.id) {
      handleGetMarkup();
      handleGetListGroupMenu();
    }
  }, [currentStore]);

  useEffect(() => {
    if (!attr || isLoadAttributeFirstTime.current) return;
    const price = form.getFieldValue("price");
    const costPrice = form.getFieldValue("costPrice");

    form.setFieldValue("price", price);
    form.setFieldValue("costPrice", costPrice);

    isLoadAttributeFirstTime.current = true;
  }, [attr, form]);

  return (
    <>
      <Form
        form={form}
        name="create-product"
        wrapperCol={{ span: 24 }}
        initialValues={getValuesFromProductDetail(markup, product, user?.merchantIds?.[0])}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <FormHeader
          title={product?.name ?? "Create new product"}
          actions={
            <>
              <Button type="text" className={styles.cancel} onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                type="text"
                className={styles.preview}
                onClick={() => {
                  setProductPreview(getBodyFromProductValues(isSellingMode, form.getFieldsValue()));
                  setPreview(true);
                }}
              >
                <GradientText text="Preview" />
              </Button>
              {/* because prv dev use this component for many case so i have to do this */}
              {(oldStatus === ProductStatusEnum.DRAFT || !oldStatus) && (
                <Button
                  type="default"
                  itemType=""
                  className={styles.draft}
                  onClick={onDraft}
                  disabled={!!loading}
                  loading={loading === "draft"}
                >
                  {loading ? "Submitting" : "Submit"} as draft
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                className={styles.save}
                disabled={!!loading}
                loading={loading === "save"}
              >
                {loading ? "Submitting" : "Submit"}
              </Button>
            </>
          }
        />

        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>Basic information</p>
            </div>
          }
        >
          <CustomInput
            name="name"
            label="Product name"
            placeholder="Enter product name"
            onBlur={removeExtraSpace("name", form)}
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            maxLength={120}
          />
          <CustomDropImageInput
            name="images"
            label="Product image"
            square
            aspect={1}
            multiple={10}
            description={
              <>
                <p>Up to 10 images</p>
                <div style={{ marginTop: "12px" }}> Image ratio will be 1:1</div>
              </>
            }
            onError={err => form?.setFields([{ name: "images", errors: [err] }])}
            setIsMatchRatio={setIsMatchRatio}
            isMatchRatio={isMatchRatio}
            rules={[
              { required: false, message: "This field is required" },
              {
                validator: (_, value) => {
                  if (isMatchRatio.every(item => item === true)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject("Image aspect ratio must be 3:2");
                  }
                },
              },
            ]}
            isEdit={product?.id ? true : false}
            form={form}
            isCheckRation={false}
          />

          <RadioBoxList
            name="canPreOrder"
            label="Pre Order Available"
            options={BINARY_OPTIONS}
            defaultValue={product?.canPreOrder || false}
            groupClassName={styles.preOrderSelect}
          />
          {/* <VideoInput
            name="videos"
            label="Update Product video"
            onError={err => form?.setFields([{ name: "videos", errors: [err] }])}
            isEdit={product?.id ? true : false}
            form={form}
          /> */}

          <Row gutter={24}>
            <Col xs={24} sm={24}>
              <Row>
                <Col span={6}>
                  <div className={styles.fieldTitle}>Product Category*</div>
                </Col>
                <Col span={18}>
                  <div className={styles.fieldContent}>{renderCateList()}</div>
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={6}>
              <CustomInput
                type="number"
                name="shipment_weight"
                label="Product weight"
                placeholder="Enter product weight"
                onBlur={removeExtraSpace("shipment_weight", form)}
                rules={[
                  {
                    required: false,
                    message: "This field is required",
                    whitespace: true,
                  },
                  {
                    validator: isPositiveNumberOrZero("shipment_weight"),
                  },
                ]}
                maxLength={120}
                prefix="Gram |"
                onKeyDown={integerOnlyInput}
                onChange={event => filterECharacterInputNumber("shipment_weight", form, event)}
              />
            </Col>
            <Col xs={24} sm={6}>
              <CustomSelect
                name="main_tags"
                label="Product Tag   "
                options={MAIN_TAG_OPTIONS}
                placeholder="Product Main Tag"
              />
            </Col>
            <Col xs={24} sm={6}>
              <CustomSelect name="groupMenuId" label="Store Menus" options={listGroupMenu} placeholder="Store Menus" />
            </Col>
            {/* <Col xs={24} sm={6}>
              <CustomSelect
                name="type"
                label="Product Type"
                options={TYPE_OF_PRODUCT_OPTIONS}
                rules={[{ required: true, message: "This field is required" }]}
                placeholder="Product Type"
              />
            </Col>
            <Col xs={24} sm={24}>
              {productType === TYPE_OF_PRODUCTS.RETAIL && (
                <CustomInput
                  type="text"
                  name="quantity"
                  label="Product quantity"
                  placeholder="0"
                  rules={[
                    { required: true, message: "This field is required" },
                    { validator: isPositiveNumber("quantity") },
                  ]}
                  onKeyDown={event => {
                    integerOnlyInput(event);
                  }}
                  onChange={event => {
                    filterECharacterInputNumber("quantity", form, event);
                  }}
                />
              )}
              <CustomInput
                name="barcode"
                label="Product barcode"
                placeholder="Enter product barcode"
                onBlur={removeExtraSpace("barcode", form)}
                rules={[
                  {
                    required: false,
                    message: "This field is required",
                    whitespace: true,
                  },
                ]}
                maxLength={120}
              />
            </Col> */}
          </Row>

          <TextArea
            name="description"
            label="Description"
            onBlur={removeExtraSpace("description", form)}
            placeholder="Tell us something about your product"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            showCount
            maxLength={3000}
          />
        </FormBox>
        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>
                Selling information{" "}
                <Switch
                  style={{ marginLeft: "15px" }}
                  checked={isSellingMode}
                  onChange={onChangeSwitch}
                  checkedChildren={"Selling Mode"}
                  unCheckedChildren={"Cost Mode"}
                />
              </p>
              <p>Mark Up: {markup + "%"} </p>
            </div>
          }
        >
          <Form.Item dependencies={["attributes"]}>
            {({ getFieldValue }) => {
              return (
                <Row gutter={24}>
                  <Col xs={12} sm={12}>
                    <CustomInput
                      type="text"
                      name="costPrice"
                      label="Product Cost Price (Amount due to Merchant)"
                      placeholder="0.00"
                      readOnly={isSellingMode ? true : false}
                      rules={[
                        { required: true, message: "This field is required" },
                        { validator: isPositiveNumberOrZero("costPrice") },
                      ]}
                      onKeyDown={event => {
                        decimalOnlyInput(event);
                      }}
                      onChange={event => {
                        filterECharacterInputDecimal("costPrice", form, event);
                        handleOnChangeInputNumberCostPrice("costPrice", event);
                      }}
                      onBlur={() => {
                        handleFormatCostPrice();
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={12}>
                    <CustomInput
                      type="text"
                      name="price"
                      label="Product Selling Price"
                      placeholder="0.00"
                      readOnly={isSellingMode ? false : true}
                      rules={[
                        { required: true, message: "This field is required" },
                        { validator: isPositiveNumberOrZero("price") },
                      ]}
                      onKeyDown={event => {
                        decimalOnlyInput(event);
                      }}
                      onChange={event => {
                        filterECharacterInputDecimal("price", form, event);
                        handleOnChangeInputNumberSellingPrice("price", event);
                      }}
                      onBlur={() => {
                        handleFormatPrice();
                      }}
                    />
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
          <Form.List name="attributes">
            {(attributes, { add, remove }: FormListOperationCustom<CreateUpdateProductValues["attributes"][0]>) => {
              return (
                <>
                  {attributes.map(({ key, name, ...restAttribute }) => {
                    return (
                      <AttributeBox
                        key={key}
                        attributeIndex={name}
                        restAttribute={restAttribute}
                        remove={remove}
                        form={form}
                        markup={markup}
                        isSellingMode={isSellingMode}
                      />
                    );
                  })}
                  <div className={styles.addAttribute}>
                    <BorderGradientButton onClick={() => add(createNewAttribute())}>
                      <GradientText text="Add more attribute" />
                    </BorderGradientButton>
                  </div>
                </>
              );
            }}
          </Form.List>
        </FormBox>
        <FormBox title="">
          <Row gutter={24}>
            <Col xs={24} sm={24}>
              <CustomInput
                name="lead_time"
                label="Lead time"
                placeholder="Lead time"
                suffix="minutes"
                rules={[
                  {
                    validator: isPositiveNumberOrZero("Lead time"),
                  },
                ]}
                onChange={event => filterECharacterInputNumber("lead_time", form, event)}
              />
            </Col>
          </Row>
        </FormBox>
      </Form>

      {productPreview && <PreviewProduct open={preview} onCancel={() => setPreview(false)} product={productPreview} />}
    </>
  );
};

export default CreateUpdateProductForm;
